// ============================================================================
// Referral Attribution Helper (Apr28 v11)
// ============================================================================
// Runs on every page load via App.js → initRefAttribution().
// If URL has ?ref=CODE, stores it in localStorage with a 30-day expiry.
// Also captures the landing product slug if URL is /product/:id?ref=CODE
// so we can show creators their top-performing products.
//
// On checkout, read the stored code (and landing product) and include in
// the order payload. Backend uses these to attribute commission and stats.
//
// HISTORY:
//   - Initially never called from App.js (silent no-op).
//   - v11 wires it in + unifies the storage key with /ref/:code's key (gg_referral)
//     so checkout reads ONE source of truth regardless of how the user landed.
// ============================================================================

const STORAGE_KEY = 'gg_referral';   // unified with ReferralLanding.js
// Apr28 v15 — attribution window reduced from 30 → 15 days.
// Rationale: 15 days is the balance Vinay picked between giving creators
// fair credit for their work AND ensuring stale clicks don't credit a
// creator the customer barely remembers. Most jewelry purchase decisions
// happen within 7-14 days of first interest.
const ATTRIBUTION_DAYS = 15;
const API_URL = process.env.REACT_APP_API_URL || '';

/**
 * Run this once when the app boots. It:
 *   1. Reads ?ref=CODE from URL
 *   2. Persists to localStorage with expiry (overwrites any existing — last-click wins)
 *   3. Captures landing product if on /product/:id
 *   4. Fires a click-tracking call to backend (silent fail if endpoint missing)
 *   5. Cleans the URL so ?ref=CODE doesn't show in address bar (cosmetic)
 */
export function initRefAttribution() {
  try {
    const params = new URLSearchParams(window.location.search);
    const refCodeRaw = String(params.get('ref') || '').trim();
    if (!refCodeRaw || refCodeRaw.length > 40) return;

    const refCode = refCodeRaw.toUpperCase();   // backend matches uppercase

    // Detect landing product if URL is /product/:id
    const productMatch = window.location.pathname.match(/^\/product\/([a-z0-9_-]+)/i);
    const landingProductId = productMatch ? productMatch[1] : null;

    // Apr28 v12 — also accept &col=SLUG (collection landing)
    const colSlugRaw = String(params.get('col') || '').trim().toLowerCase();
    const collectionSlug = colSlugRaw && colSlugRaw.length <= 80 ? colSlugRaw : null;

    // Save to localStorage with the unified key shape
    const expiresAt = Date.now() + ATTRIBUTION_DAYS * 24 * 60 * 60 * 1000;
    const payload = {
      code: refCode,                  // matches ReferralLanding.js shape
      timestamp: Date.now(),
      expiresAt,
      landingProductId,
      collectionSlug,                 // NEW v12
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));

    // Fire-and-forget click tracking (silent fail)
    fetch(`${API_URL}/api/creators/track-click`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        refCode,
        landingUrl: window.location.href,
        landingProductId,
        collectionSlug,
        referrer: document.referrer || '',
      }),
    }).catch(() => {});

    // Clean URL — remove ?ref= and &col= from the visible address bar
    if (window.history && window.history.replaceState) {
      params.delete('ref');
      params.delete('col');
      const newSearch = params.toString();
      const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash;
      window.history.replaceState({}, document.title, newUrl);
    }
  } catch (_) {
    // localStorage might be disabled / private mode — silent fail
  }
}

/**
 * Get the currently stored refCode if within 30-day window.
 * Returns null if expired or not set.
 */
export function getStoredRefCode() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data) return null;
    // Support both shapes — old code (refCode) and new (code)
    const code = data.code || data.refCode || null;
    const expiresAt = data.expiresAt || 0;
    if (!code || !expiresAt) return null;
    if (Date.now() > expiresAt) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return code;
  } catch (_) {
    return null;
  }
}

/**
 * Get the landing product ID (the product the customer landed on via the
 * affiliate link). Returns null if no referral or landing wasn't a product.
 */
export function getStoredLandingProductId() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || !data.expiresAt || Date.now() > data.expiresAt) return null;
    return data.landingProductId || null;
  } catch (_) {
    return null;
  }
}

/**
 * Apr28 v12 — get the collection slug if the customer landed on a curated page.
 * Returns null if no referral or landing wasn't a collection.
 */
export function getStoredCollectionSlug() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || !data.expiresAt || Date.now() > data.expiresAt) return null;
    return data.collectionSlug || null;
  } catch (_) {
    return null;
  }
}

/**
 * Clear stored refCode (e.g., after a successful order has been attributed).
 * Optional — we keep it for the full 15 days in case the same user orders again.
 */
export function clearRefCode() {
  try { localStorage.removeItem(STORAGE_KEY); } catch (_) {}
}

/**
 * Fetch display info about the current refCode.
 */
export async function getRefDisplayInfo() {
  const refCode = getStoredRefCode();
  if (!refCode) return null;
  try {
    const res = await fetch(`${API_URL}/api/creators/ref/${encodeURIComponent(refCode)}`);
    if (!res.ok) return null;
    const data = await res.json();
    if (!data.valid) return null;
    return data;
  } catch (_) {
    return null;
  }
}
